import { albumWindowSize } from '../../features/browse/albumWindow';
import { invalidateExploreSummary } from '../explore/exploreRefresh';
import BrowseShell, { BrowseSegments } from '../../features/browse/BrowseShell';
import BrowseFilters from '../../features/browse/BrowseFilters';
import { browseParams, albumDefaultSort } from '../../features/browse/contracts';
import { AccountBoundary } from '../../features/settings/shared';
import {
  useBrowseSession,
  useBrowseContext,
  useBrowseQuery,
  useBrowsePosition,
} from '../../features/browse/useBrowseList';
import { albumCategoryParams } from '../../features/browse/listModel';
import BrowseListState, { BrowseEmpty } from '../../features/browse/BrowseListState';
import {
  mergeReleaseCampaigns,
  sponsoredBlocks,
  insertionSlots,
} from '../../features/browse/sponsored';
import { BrowseInlineAd, BrowseSponsoredAlbum } from '../../features/browse/BrowseSponsored';
import { useSponsoredPlacements } from '../../features/ads/useSponsoredPlacements';
import { useWebLocale } from '../../contexts/WebLocaleContext';
import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Box, useMediaQuery } from '@mui/material';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import {
  closestCenter,
  DndContext,
  KeyboardSensor,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { restrictToParentElement } from '@dnd-kit/modifiers';
import {
  arrayMove,
  rectSortingStrategy,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import useTrackPageView from '../../hooks/useTrackPageView';
import { trackPageView } from '../../lib/gaEvents';
import { PAGE_TYPE } from '../../constants/gaEnums';
import { useSearchParams, useNavigate } from 'react-router-dom';
import AlbumVerticalCard from '../../components/card/AlbumVerticalCard';
import { reviewsApi } from '../../api/reviews';
import { adminApi } from '../../api/admin';
import { useQueryClient } from '@tanstack/react-query';
import BrowseLoadMore from '../../features/browse/BrowseLoadMore';
import { useBrowseStream } from '../../features/browse/useBrowseStream';
import { useUserStore } from '../../stores/userStore';
import BasicButton from '../../components/button/BasicButton';
import SEO from '../../components/common/SEO';
import { SEO_DESCRIPTIONS } from '../../constants/seoDescriptions';
import { Helmet } from 'react-helmet-async';
import { SITE_URL } from '../../utils/env';

const SortableAlbumItem = ({ item, children }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: item.id,
  });

  return (
    <Box
      ref={setNodeRef}
      sx={{
        minWidth: 0,
        position: 'relative',
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.65 : 1,
        zIndex: isDragging ? 2 : 0,
      }}
    >
      <Box
        {...attributes}
        {...listeners}
        sx={{
          position: 'absolute',
          top: { xs: 12, lg: 16 },
          left: { xs: 12, lg: 16 },
          zIndex: 11,
          width: { xs: 24, lg: 32 },
          height: { xs: 24, lg: 32 },
          borderRadius: '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: 'rgba(255, 255, 255, 0.92)',
          boxShadow: '0 1px 4px rgba(0, 0, 0, 0.18)',
          color: 'var(--pp-text-secondary)',
          cursor: isDragging ? 'grabbing' : 'grab',
          touchAction: 'none',
        }}
      >
        <DragIndicatorIcon sx={{ fontSize: { xs: 18, lg: 22 } }} />
      </Box>
      {children}
    </Box>
  );
};

const Albums = () => <AccountBoundary component={AlbumsBoundary} />;
function AlbumsBoundary({ user }) {
  const { locale } = useWebLocale();
  const memory = useBrowseSession('albums');
  if (useUserStore.getState().user?.id !== user?.id) return null;
  return <AlbumsView key={`${locale}:${memory.entry.version}`} {...memory} />;
}
const AlbumsView = ({ entry, returning }) => {
  const { locale } = useWebLocale();
  const en = locale === 'en';
  const [searchParams, setSearchParams] = useSearchParams();
  const filters = browseParams(searchParams, 'albums');
  const { region: regionTab, category: categoryTab, type: subTab, sort: sortBy, period } = filters;
  const context = useBrowseContext(entry);
  context.tabs[categoryTab] = { region: regionTab, sort: sortBy, page: filters.page };
  const toAlbumsCategory = (category, region = regionTab) =>
    category === 'releases' ? `${region}_new_releases` : `${region}_recommended`;
  useTrackPageView({
    pageType: PAGE_TYPE.REVIEWS_MAIN,
    reviewsCategory: toAlbumsCategory(categoryTab),
  });
  const [saving, setSaving] = useState(false);
  const savingRef = useRef(false);
  const [saveError, setSaveError] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const editRoute = useRef(null);
  const [albumsToDelete, setAlbumsToDelete] = useState(new Set());
  const [orderedAlbums, setOrderedAlbums] = useState([]);

  const navigate = useNavigate();
  const { isAdmin, user } = useUserStore();
  const queryClient = useQueryClient();

  // breakpoint 감지
  const isXs = useMediaQuery('(max-width:767px)');
  const isMd = useMediaQuery('(min-width:768px) and (max-width:1199px)');
  const columns = isXs ? 2 : isMd ? 4 : 5;

  const isAdminManageableTab = categoryTab === 'releases' || categoryTab === 'recommended';
  const isRecommendedOrderMode =
    categoryTab === 'recommended' &&
    regionTab === 'all' &&
    subTab === 'albums' &&
    sortBy === 'latest';
  const canDragRecommendedAlbums = isEditing && isRecommendedOrderMode;
  const editingRecommendedTargetSize = isEditing && isRecommendedOrderMode ? 200 : null;

  const {
    region: queryRegion,
    category: queryCategory,
    type: queryType,
    sort: querySort,
    period: queryPeriod,
  } = filters;
  const querySize = editingRecommendedTargetSize || 20;
  const legacyAds = useSponsoredPlacements('ALBUMS_RELEASES');
  const krAds = useSponsoredPlacements('ALBUMS_RELEASES_KR');
  const globalAds = useSponsoredPlacements('ALBUMS_RELEASES_GLOBAL');
  const pickAds = useSponsoredPlacements('ALBUMS_RECOMMENDED');
  const campaigns =
    queryCategory === 'recommended'
      ? pickAds
      : mergeReleaseCampaigns([
          ...legacyAds,
          ...(queryRegion !== 'global' ? krAds : []),
          ...(queryRegion !== 'korean' ? globalAds : []),
        ]);
  const adPlan = JSON.stringify({
    nativeAdAfter: campaigns
      .filter((c) => c.placements[0]?.format === 'NATIVE_ALBUM' && c.albums?.length)
      .flatMap(insertionSlots)
      .sort((a, b) => a - b),
    advertisedIds: [...new Set(campaigns.flatMap((c) => (c.albums || []).map((a) => a.id)))].sort(
      (a, b) => a - b,
    ),
  });
  // One recommendation session across pages; fresh when its input contract changes.
  const sessionInputs = JSON.stringify([queryRegion, queryType, queryPeriod, querySort, adPlan]);
  if (queryCategory === 'releases' && context.releaseSession?.inputs !== sessionInputs)
    context.releaseSession = {
      inputs: sessionInputs,
      value: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
    };
  const recommendationSession = context.releaseSession?.value;

  const listArgs = {
    ...filters,
    size: querySize,
    ...(editingRecommendedTargetSize ? { page: 1 } : {}),
    ...(columns > 2 && !editingRecommendedTargetSize
      ? {
          windowSize: albumWindowSize(columns, campaigns),
          windowStep: Math.ceil(20 / columns) * columns,
          windowExcludedIds: [
            ...new Set(campaigns.flatMap((c) => (c.albums || []).map((a) => a.id))),
          ].sort((a, b) => a - b),
        }
      : {}),
    ...(queryCategory === 'releases' && querySort === 'recommended'
      ? { recommendationSession, ...JSON.parse(adPlan) }
      : {}),
  };
  const identity = `${locale}:${entry.version}:${context.id}`;
  const single = useBrowseQuery('albums', listArgs, identity, {
    enabled: Boolean(editingRecommendedTargetSize),
  });
  const stream = useBrowseStream('albums', listArgs, identity, {
    enabled: !editingRecommendedTargetSize,
  });
  const query = editingRecommendedTargetSize ? single : stream;
  const { data: reviewsData } = query;
  const isFetching = query.busy ?? query.isFetching;
  const position = useBrowsePosition({ entry, returning, ready: !query.isPending });

  const albums = useMemo(() => reviewsData?.albums ?? [], [reviewsData?.albums]);
  const visibleAlbums = canDragRecommendedAlbums ? orderedAlbums : albums;

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(TouchSensor, {
      activationConstraint: {
        delay: 160,
        tolerance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const initializedOrder = useRef(null);
  useEffect(() => {
    // Initialise a reorder draft once after its 200-row query arrives.
    if (!isEditing) {
      initializedOrder.current = null;
      setOrderedAlbums(albums);
    } else if (
      canDragRecommendedAlbums &&
      !isFetching &&
      query.isSuccess &&
      initializedOrder.current !== querySize
    ) {
      initializedOrder.current = querySize;
      setOrderedAlbums(albums);
    }
  }, [albums, canDragRecommendedAlbums, isEditing, isFetching, query.isSuccess, querySize]);

  const buildParams = ({
    region = regionTab,
    category = categoryTab,
    type = subTab,
    sort,
    page = '1',
    periodValue = period,
  }) => {
    const defaultSort = albumDefaultSort(category);
    const params = {
      region,
      category,
      type,
      sort: sort || defaultSort,
      page,
    };
    params.period = periodValue;
    return params;
  };

  const handleCategoryTabChange = (newCategory) => {
    if (savingRef.current) return;
    trackPageView(
      { pageType: PAGE_TYPE.REVIEWS_MAIN, reviewsCategory: toAlbumsCategory(newCategory) },
      user,
    );
    setIsEditing(false);
    setAlbumsToDelete(new Set());
    setSaveError(false);
    setSearchParams(albumCategoryParams(filters, newCategory, context.tabs[newCategory]));
  };

  const handleUploadClick = () => {
    const params = new URLSearchParams({
      region: regionTab,
      category: categoryTab,
      type: subTab,
      sort: sortBy,
      page: '1',
    });
    if (categoryTab === 'releases') {
      params.set('period', period);
    }
    navigate(`/albums/upload?${params.toString()}`);
  };

  const handleEditClick = () => {
    setSaveError(false);
    editRoute.current = searchParams.toString();
    if (categoryTab === 'recommended' && !isRecommendedOrderMode) {
      const target = buildParams({
        region: 'all',
        category: 'recommended',
        type: 'albums',
        sort: 'latest',
        page: '1',
      });
      editRoute.current = new URLSearchParams(target).toString();
      setSearchParams(target);
    }
    setOrderedAlbums(albums);
    setIsEditing(true);
  };

  useEffect(() => {
    if (isEditing && editRoute.current !== searchParams.toString() && !savingRef.current) {
      setIsEditing(false);
      setAlbumsToDelete(new Set());
      setSaveError(false);
    }
  }, [isEditing, searchParams]);

  const handleSaveClick = async () => {
    if (savingRef.current || isFetching || !query.isSuccess || !entry.current()) return;
    savingRef.current = true;
    setSaving(true);
    setSaveError(false);
    try {
      if (canDragRecommendedAlbums) {
        const orderedAlbumIds = orderedAlbums
          .map((album) => album.id)
          .filter((albumId) => !albumsToDelete.has(albumId));
        await adminApi.updateRecommendedAlbumOrder(orderedAlbumIds);
      }

      if (!entry.current()) return;
      if (albumsToDelete.size > 0) {
        const albumIdsToExclude = Array.from(albumsToDelete);
        if (categoryTab === 'recommended') {
          await reviewsApi.excludeRecommends({ ids: albumIdsToExclude });
        } else {
          await reviewsApi.excludeNewRelease({ ids: albumIdsToExclude });
        }
      }

      if (!entry.current()) return;
      setAlbumsToDelete(new Set());
      setIsEditing(false);
      void invalidateExploreSummary(queryClient);
      queryClient.invalidateQueries({ queryKey: ['reviewsAlbums'] });
      queryClient.invalidateQueries({ queryKey: ['home'] });
      queryClient.invalidateQueries({ queryKey: ['homeAlbums'] });
    } catch (error) {
      if (entry.current()) setSaveError(true);
    } finally {
      savingRef.current = false;
      if (entry.current()) setSaving(false);
    }
  };

  const handleCancelClick = () => {
    if (savingRef.current) return;
    setSaveError(false);
    setIsEditing(false);
    setAlbumsToDelete(new Set());
    setOrderedAlbums(albums);
  };

  const handleMarkForDeletion = (albumId) => {
    setAlbumsToDelete((prev) => new Set([...prev, albumId]));
  };

  const handleUndoDeletion = (albumId) => {
    setAlbumsToDelete((prev) => {
      const newSet = new Set(prev);
      newSet.delete(albumId);
      return newSet;
    });
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    setOrderedAlbums((items) => {
      const oldIndex = items.findIndex((item) => item.id === active.id);
      const newIndex = items.findIndex((item) => item.id === over.id);
      if (oldIndex < 0 || newIndex < 0) return items;
      return arrayMove(items, oldIndex, newIndex);
    });
  };

  const renderAlbumCard = (item, index) => (
    <AlbumVerticalCard
      albumId={item.id}
      sourceId={item.id}
      referrerSection={toAlbumsCategory(categoryTab)}
      albumSource={'local'}
      slug={item.slug}
      imageUrl={item.imageUrl}
      albumName={item.title}
      artistId={item.artist?.sourceId}
      artistDbId={item.artist?.id}
      artistSource={item.artist?.artistSource}
      artistName={item.artist?.name || (en ? 'Unknown artist' : '아티스트 정보 없음')}
      artistSlug={item.artist?.slug}
      albumAvgRating={item.averageRating}
      albumGenres={item.genres || item.genreNames || item.albumGenres || []}
      showGenres
      hideBookmarkCount
      commentCount={item.reviewCount}
      bookmarkCount={item.bookmarkCount}
      releaseDate={item.releaseDate}
      contentRating={item.contentRating}
      isRecommended={item.isRecommended}
      isStarred={item.isStarred}
      isEditMode={isEditing}
      isMarkedForDeletion={albumsToDelete.has(item.id)}
      onMarkForDeletion={handleMarkForDeletion}
      onUndoDeletion={handleUndoDeletion}
      appRatios
      showNewRelease={queryCategory === 'releases'}
    />
  );

  const renderAlbumGrid = (items) => (
    <div className="pp-browse-grid">
      {(canDragRecommendedAlbums
        ? items.map((item) => ({ kind: 'row', tiles: [{ kind: 'organic', item }] }))
        : sponsoredBlocks(items, campaigns, columns)
      ).flatMap((block, blockIndex) =>
        block.kind === 'inline'
          ? [
              <div className="pp-browse-full" key={`ad-${block.campaign.id}-${block.occurrence}`}>
                <BrowseInlineAd campaign={block.campaign} />
              </div>,
            ]
          : block.tiles.map((entry, index) =>
              entry.kind === 'sponsored' ? (
                <BrowseSponsoredAlbum
                  key={`native-${entry.campaign.id}-${entry.occurrence}`}
                  campaign={entry.campaign}
                  releases={queryCategory === 'releases'}
                />
              ) : canDragRecommendedAlbums ? (
                <SortableAlbumItem key={entry.item.id} item={entry.item}>
                  {renderAlbumCard(entry.item)}
                </SortableAlbumItem>
              ) : (
                <div key={`${entry.item.id}-${blockIndex}-${index}`} style={{ minWidth: 0 }}>
                  {renderAlbumCard(entry.item)}
                </div>
              ),
            ),
      )}
    </div>
  );

  return (
    <BrowseShell
      active="albums"
      swipe={{
        values: ['releases', 'recommended'],
        value: categoryTab,
        onChange: handleCategoryTabChange,
      }}
    >
      <div {...position}>
        <SEO title="Albums" description={SEO_DESCRIPTIONS.REVIEWS} url={`${SITE_URL}/albums`} />
        <Helmet>
          <script type="application/ld+json">
            {JSON.stringify({
              '@context': 'https://schema.org',
              '@type': 'BreadcrumbList',
              itemListElement: [
                { '@type': 'ListItem', position: 1, name: 'Postpop', item: SITE_URL },
                { '@type': 'ListItem', position: 2, name: 'Albums' },
              ],
            })}
          </script>
        </Helmet>
        {albums.length > 0 && (
          <Helmet>
            <script type="application/ld+json">
              {JSON.stringify({
                '@context': 'https://schema.org',
                '@type': 'ItemList',
                itemListElement: albums
                  .filter((item) => item.imageUrl || item.albumImageUrl)
                  .map((item, index) => ({
                    '@type': 'ListItem',
                    name: item.title || item.albumTitle,
                    image: item.imageUrl || item.albumImageUrl,
                    url: item.slug
                      ? `${SITE_URL}/reviews/${item.slug}`
                      : `${SITE_URL}/reviews/local/${item.id || item.albumId}`,
                    position: index + 1,
                  })),
              })}
            </script>
          </Helmet>
        )}
        <div className="pp-browse-toolbar">
          <div className="pp-browse-primary-tools">
            <BrowseSegments
              options={[
                ['releases', en ? 'New releases' : '최신 앨범'],
                ['recommended', en ? 'Postpop picks' : '포스트팝 추천'],
              ]}
              value={categoryTab}
              onChange={handleCategoryTabChange}
            />
            {/* 버튼 영역 (admin only) */}
            {isAdmin && isAdminManageableTab && (
              <Box className="pp-browse-admin-actions" sx={{ display: 'flex', gap: 1 }}>
                {!isEditing && (
                  <BasicButton
                    variant="outlined"
                    onClick={handleUploadClick}
                    sx={{ padding: '6px 10px', minHeight: 36, fontSize: 12, fontWeight: 'medium' }}
                  >
                    업로드
                  </BasicButton>
                )}
                {isEditing ? (
                  <>
                    <BasicButton
                      variant="outlined"
                      disabled={saving || isFetching || !query.isSuccess}
                      onClick={handleSaveClick}
                      sx={{
                        padding: '6px 10px',
                        minHeight: 36,
                        fontSize: 12,
                        fontWeight: 'medium',
                      }}
                    >
                      저장 {albumsToDelete.size > 0 && `(${albumsToDelete.size})`}
                    </BasicButton>
                    <BasicButton
                      variant="outlined"
                      disabled={saving}
                      onClick={handleCancelClick}
                      sx={{
                        padding: '6px 10px',
                        minHeight: 36,
                        fontSize: 12,
                        fontWeight: 'medium',
                      }}
                    >
                      취소
                    </BasicButton>
                  </>
                ) : (
                  <BasicButton
                    variant="outlined"
                    disabled={isFetching || !query.isSuccess}
                    onClick={handleEditClick}
                    sx={{ padding: '6px 10px', minHeight: 36, fontSize: 12, fontWeight: 'medium' }}
                  >
                    수정
                  </BasicButton>
                )}
              </Box>
            )}
          </div>

          {!isEditing && (
            <>
              <BrowseFilters
                key={searchParams.toString()}
                kind="albums"
                value={filters}
                onChange={(next) => {
                  if (next.type !== subTab || next.period !== period)
                    Object.values(context.tabs).forEach((tab) => {
                      tab.page = 1;
                    });
                  setSearchParams({ ...next, page: '1' });
                }}
              />
            </>
          )}
        </div>
        {saveError && (
          <div role="alert" className="pp-browse-list-status">
            {en
              ? 'Could not save changes. Your edits are still here; try again.'
              : '변경사항을 저장하지 못했어요. 편집 내용은 유지되니 다시 시도해 주세요.'}
          </div>
        )}
        <BrowseListState query={query} kind="albums">
          {!query.isError && visibleAlbums.length === 0 && campaigns.length === 0 ? (
            <BrowseEmpty kind="albums" />
          ) : (
            <>
              {canDragRecommendedAlbums ? (
                <DndContext
                  sensors={sensors}
                  collisionDetection={closestCenter}
                  modifiers={[restrictToParentElement]}
                  onDragEnd={handleDragEnd}
                >
                  <SortableContext
                    items={visibleAlbums.map((item) => item.id)}
                    strategy={rectSortingStrategy}
                  >
                    {renderAlbumGrid(visibleAlbums)}
                  </SortableContext>
                </DndContext>
              ) : (
                renderAlbumGrid(visibleAlbums)
              )}
              {!canDragRecommendedAlbums && (
                <BrowseLoadMore
                  key={JSON.stringify([identity, listArgs])}
                  query={stream}
                  disabled={saving}
                  autoDisabled={isEditing}
                  restored={returning && entry.y !== undefined}
                />
              )}
            </>
          )}
        </BrowseListState>
      </div>
    </BrowseShell>
  );
};

export default Albums;
